import { Component, Input, ViewChild } from '@angular/core';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    Lotam,
    NoticeDto,
    NoticesService,
    PersonsOrganisationDto,
    WorkTracker,
    hasAnyPermission
} from '@flyfreely-portal-ui/flyfreely';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { LotamDataService } from '../lotam-data.service';
import { LotamEditComponent } from '../lotam-edit/lotam-edit.component';
import { MissionNoticesDataService } from '../mission-notices-data.service';

@Component({
    selector: 'flyfreely-portal-ui-lotam-details-dialogue',
    templateUrl: './lotam-details-dialogue.component.html',
    styleUrls: ['./lotam-details-dialogue.component.scss'],
    providers: [MissionNoticesDataService]
})
export class LotamDetailsDialogueComponent {
    @Input() organisationId?: number;
    @Input() lotamId?: number;
    /**
     * If this is a new entry, what audience, or what clone source
     */
    @Input() isNew: typeof LotamEditComponent.prototype.isNew = false;

    lotam?: Lotam;
    organisation?: PersonsOrganisationDto;
    isEditing = false;

    @ViewChild('lotamEdit', { static: false })
    lotamEdit?: LotamEditComponent;

    canDelete = false;

    working = false;
    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private workspaceStateService: WorkspaceStateService,
        private noticesService: NoticesService,
        private modal: BsModalRef<LotamDetailsDialogueComponent>,
        modalOptions: ModalOptions,
        private logging: FlyFreelyLoggingService,
        private lotamDataService: LotamDataService,
        private commonDialoguesService: CommonDialoguesService
    ) {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation =>
                this.setOrganisation(organisation.organisation)
            );
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        modalOptions.closeInterceptor = () => {
            if (!this.isEditing) {
                return Promise.resolve();
            }
            if (this.lotamEdit == null) {
                return Promise.reject();
            }

            return this.lotamEdit.tryCancel().then(
                cancel => {
                    if (this.isNew) {
                        return Promise.resolve();
                    }
                    this.isEditing = false;
                    return Promise.reject();
                },
                dontCancel => Promise.reject()
            );
        };
    }

    ngOnInit() {
        if (this.isNew == null && (this.lotam != null || this.lotamId != null)) {
            this.isNew = false;
        }

        if (this.isNew == null) {
            throw new Error('isNew not set');
        }

        if (this.isNew) {
            this.isEditing = true;
        } else {
            this.refreshLotam();
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private setOrganisation(organisation: PersonsOrganisationDto) {
        this.organisation = organisation;
        this.canDelete = hasAnyPermission(
            this.organisation,
            PersonsOrganisationDto.Permissions.NOTICE_EDIT
        );
    }

    private refreshLotam() {
        if (this.lotamId == null || this.organisationId == null) {
            return;
        }

        this.noticesService
            .findNoticeById(this.lotamId, this.organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: lotam =>
                    (this.lotam = {
                        ...lotam,
                        isAcknowledged:
                            lotam.currentUserAcknowledgementTime != null
                    }),
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while refreshing lotam: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    onAcknowledge(event: Event) {
        event.preventDefault();
        if (this.lotam == null) {
            return;
        }

        if (this.lotam.isAcknowledged) {
            return;
        }
        this.lotamDataService
            .acknowledgeLotam(this.lotam.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: () => {
                    this.logging.success(
                        `Successfully acknowledge LOTAM ${this.lotam?.subject}`
                    );
                    this.modal.hide();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while acknowledge LOTAM: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    onCancel() {
        this.modal.hide();
    }

    toggleViewEdit() {
        this.isEditing = !this.isEditing;
    }

    onDelete() {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Delete LOTAM',
                `Do you wish to delete this LOTAM? It will be permanently removed from all missions, and acknowledgement history will be lost.`,
                'Yes',
                () =>
                    firstValueFrom(
                        this.lotamDataService
                            .deleteLotam(this.lotam.id)
                            .pipe(takeUntil(this.ngUnsubscribe$))
                    )
            )
            .then(
                results => {
                    this.logging.success(`Deleted LOTAM`);
                    this.modal.hide();
                },
                (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error deleting LOTAM: ${error.message}`
                    )
            );
    }

    onEditingCancelled() {
        if (this.isNew) {
            this.modal.hide();
        } else {
            this.isEditing = false;
        }
    }

    onLotamUpdated(updatedLotam: Lotam) {
        if (this.isNew) {
            this.modal.hide();
            return;
        }
        this.lotam = {
            ...updatedLotam,
            isAcknowledged: updatedLotam.currentUserAcknowledgementTime != null
        };
        this.isEditing = false;
    }

    protected readonly NoticeDto = NoticeDto;
}
